import { Alert, Box, Button, IconButton, Stack } from '@mui/material'
import { identity, ifElse, pipe, propIs } from 'ramda'
import { useEffect } from 'react'
import { FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { Case, Else, If, Switch, Then, When } from 'react-if'
import { useNavigate, useParams } from 'react-router-dom'

import {
  ComponentSelector,
  Icon,
  VailAccordion,
  VailNumberInput,
  VailSelect,
  VailSwitch,
  VailTextField,
} from '@/components'
import LanguageSelector from '@/components/LanguageSelector'
import { useOrg, useUrlParams } from '@/hooks'
import { BasicCustomComponentSettings } from '@/screens/ComponentLibrary/components'
import {
  useCreateCustomComponent,
  useGetCustomComponent,
  useUpdateCustomComponent,
} from '@/screens/ComponentLibrary/hooks'
import { safeCypressID } from '@/utils'
import { ComponentType, ReleaseBundleInput } from '@/utils/api'
import modifyPath from '@/utils/modifyPath'

import {
  localizedArray,
  localizedString,
  SupportedLocales,
} from '../../types/customComponents'

export enum ExternalLinkType {
  url = 'url',
  webpage = 'webpage',
  phone = 'phone',
}

export type HomeBaseSettings = {
  egOrder: string[]
  interactionsBeforeSentiment: number
  HomeBaseMenuIntroText: localizedArray
  HomeBaseMenuText: localizedArray
  ShowUserMoreItemsText: localizedString
  NumberOfEGsPerMenu: number
  SkipMenuOnSingleEG: boolean
  customRecipeOutro: ReleaseBundleInput | null
  HomeBaseMenuOutro: {
    InteractionsToSurfaceOutroButton: number
    OutroButtonText: localizedString
    OutroText: localizedArray
    ReturnToHomeBaseMenuButtonText: localizedString
    HomeBasePersistentButtons: {
      label: localizedString
      type: ExternalLinkType
      value: string
    }[]
  }
  useCustomRecipeOutro: boolean
}

export type HomeBaseForm = {
  settings: HomeBaseSettings
  name: string
  description: string
  type: ComponentType
}

export const Settings = () => {
  const { componentID, orgID } = useParams()
  const org = useOrg(orgID)
  const [{ lang = 'en' }] = useUrlParams()

  const current = useGetCustomComponent<HomeBaseSettings>(componentID)
  const create = useCreateCustomComponent<HomeBaseSettings>(orgID)
  const update = useUpdateCustomComponent<HomeBaseSettings>(componentID)
  const form = useForm<HomeBaseForm>({
    defaultValues: {
      settings: {
        HomeBaseMenuOutro: {
          HomeBasePersistentButtons: [
            {
              label: { en: '' },
              type: ExternalLinkType.url,
              value: '',
            },
          ],
        },
        SkipMenuOnSingleEG: false,
        customRecipeOutro: null,
        useCustomRecipeOutro: false,
      },
    },
  })
  const navigate = useNavigate()
  const { reset } = form

  const onSubmit = form.handleSubmit(async (data) => {
    if (componentID) return update.mutateAsync({ ...data, id: componentID })
    const res = await create.mutateAsync(data)
    return navigate(`../${res?.id}`)
  })
  const fieldArray = useFieldArray({
    control: form.control,
    name: 'settings.HomeBaseMenuOutro.HomeBasePersistentButtons',
    rules: {
      minLength: { message: 'At least one button is required', value: 1 },
      required: {
        message: 'Please add at least one static outro button',
        value: true,
      },
    },
  })
  useEffect(() => {
    if (!current.data) return
    const { settings, name, description, type } = current.data

    const updateSettings = pipe(
      ifElse(
        propIs(String, 'preMenuText'),
        modifyPath(['preMenuText'], ['HomeBaseMenuIntroText', 'en', 0]),
        identity
      ),
      ifElse(
        propIs(Number, 'egsPerMenu'),
        modifyPath(['egsPerMenu'], ['NumberOfEGsPerMenu']),
        identity
      ),
      ifElse(
        propIs(String, 'showUserMoreItemsText'),
        modifyPath(['showUserMoreItemsText'], ['ShowUserMoreItemsText', 'en']),
        identity
      ),
      ifElse(
        propIs(Boolean, 'skipMenuOnSingleEG'),
        modifyPath(['skipMenuOnSingleEG'], ['SkipMenuOnSingleEG']),
        identity
      ),
      ifElse(
        propIs(Number, 'interactionsToSurfaceOutroButton'),
        modifyPath(
          ['interactionsToSurfaceOutroButton'],
          ['HomeBaseMenuOutro', 'InteractionsToSurfaceOutroButton']
        ),
        identity
      ),
      ifElse(
        propIs(String, 'outroButtonText'),
        modifyPath(
          ['outroButtonText'],
          ['HomeBaseMenuOutro', 'OutroButtonText', 'en']
        ),
        identity
      ),
      ifElse(
        propIs(String, 'outroText'),
        modifyPath(['outroText'], ['HomeBaseMenuOutro', 'OutroText', 'en', 0]),
        identity
      ),
      ifElse(
        propIs(String, 'returnToHomeBaseMenuButtonText'),
        modifyPath(
          ['returnToHomeBaseMenuButtonText'],
          ['HomeBaseMenuOutro', 'ReturnToHomeBaseMenuButtonText', 'en']
        ),
        identity
      ),
      ifElse(
        propIs(Array, 'externalButtons'),
        modifyPath(
          ['externalButtons'],
          ['HomeBaseMenuOutro', 'HomeBasePersistentButtons']
        ),
        identity
      )
    )

    reset({ description, name, settings: updateSettings(settings), type })
  }, [reset, current.data])

  const languagesSupported = org.data?.languagesSupported || ['en']

  return (
    <FormProvider {...form}>
      <Stack component="form" height="100%" rowGap={2} onSubmit={onSubmit}>
        <Button
          id={safeCypressID('save button')}
          sx={{ alignSelf: 'flex-end', mb: 2 }}
          type="submit"
        >
          Save
        </Button>
        <BasicCustomComponentSettings componentType={ComponentType.homeBase} />
        <VailAccordion defaultExpanded title="Configuration">
          <VailSwitch
            control={form.control}
            label="Skip Menu on Single EG"
            name="settings.SkipMenuOnSingleEG"
          />
          <LanguageSelector />
          <VailNumberInput
            control={form.control}
            defaultValue={2}
            helperText={
              <span>
                {form.watch('settings.interactionsBeforeSentiment') < 0
                  ? 'HBM will never go to sentiment check'
                  : 'The number of interactions required to see the sentiment check, use -1 to disable it'}
              </span>
            }
            label="Interactions Before Sentiment"
            name="settings.interactionsBeforeSentiment"
          />
          <Switch>
            {languagesSupported.map((orgLang) => (
              <Case key={orgLang} condition={lang === orgLang}>
                <VailTextField
                  multiline
                  control={form.control}
                  label="Initial Menu Text"
                  minRows={3}
                  name={`settings.HomeBaseMenuIntroText.${
                    orgLang as SupportedLocales
                  }.0`}
                />
              </Case>
            ))}
          </Switch>
          <Switch>
            {languagesSupported.map((orgLang) => (
              <Case key={orgLang} condition={lang === orgLang}>
                <VailTextField
                  multiline
                  control={form.control}
                  label="Subsequent Menu Text"
                  minRows={3}
                  name={`settings.HomeBaseMenuText.${
                    orgLang as SupportedLocales
                  }.0`}
                />
              </Case>
            ))}
          </Switch>
          <Box
            columnGap={1}
            display="grid"
            gridTemplateColumns={{ md: '1fr 1fr', xs: '1fr' }}
            rowGap={2}
          >
            <VailNumberInput
              control={form.control}
              defaultValue={0}
              label="Number of Engagement Goals per Menu"
              name="settings.NumberOfEGsPerMenu"
              rules={{
                validate: {
                  positive: (v) => {
                    const safeValue =
                      typeof v === 'string' ? parseInt(v) : (v as number)
                    return safeValue >= 1 || 'Should be greater than 0'
                  },
                },
              }}
            />
            <Switch>
              {languagesSupported.map((orgLang) => (
                <Case key={orgLang} condition={lang === orgLang}>
                  <VailTextField
                    control={form.control}
                    label="Show user more items text"
                    name={`settings.ShowUserMoreItemsText.${
                      orgLang as SupportedLocales
                    }`}
                  />
                </Case>
              ))}
            </Switch>
            <VailNumberInput
              control={form.control}
              defaultValue={0}
              label="Number of Interactions to Surface Outro Button"
              name="settings.HomeBaseMenuOutro.InteractionsToSurfaceOutroButton"
              rules={{
                validate: {
                  positive: (v) => {
                    const safeValue =
                      typeof v === 'string' ? parseInt(v) : (v as number)
                    return safeValue >= 0 || 'Should be greater than -1'
                  },
                },
              }}
            />
            <Switch>
              {languagesSupported.map((orgLang) => (
                <Case key={orgLang} condition={lang === orgLang}>
                  <VailTextField
                    control={form.control}
                    label="Outro Button Text"
                    name={`settings.HomeBaseMenuOutro.OutroButtonText.${
                      orgLang as SupportedLocales
                    }`}
                  />
                </Case>
              ))}
            </Switch>

            <Switch>
              {languagesSupported.map((orgLang) => (
                <Case key={orgLang} condition={lang === orgLang}>
                  <VailTextField
                    control={form.control}
                    label="Return to Menu Button"
                    name={`settings.HomeBaseMenuOutro.ReturnToHomeBaseMenuButtonText.${
                      orgLang as SupportedLocales
                    }`}
                  />
                </Case>
              ))}
            </Switch>

            <Box
              columnGap={1}
              display="grid"
              gridTemplateColumns={{ md: '1fr 1fr', xs: '1fr' }}
              rowGap={2}
              sx={{ gridColumn: 'span 2' }}
            >
              <VailSwitch
                control={form.control}
                label="Use custom outro"
                name="settings.useCustomRecipeOutro"
              />
              <If condition={form.watch('settings.useCustomRecipeOutro')}>
                <Then>
                  <Box
                    sx={{
                      display: 'grid',
                      gridColumn: 'span 2',
                      rowGap: 2,
                    }}
                  >
                    <ComponentSelector
                      optional
                      control={form.control}
                      name="settings.customRecipeOutro"
                      type={ComponentType.recipe}
                    />
                    {form.watch('settings.customRecipeOutro.name') && (
                      <Button
                        sx={{ flexBasis: '3rem' }}
                        variant="outlined"
                        onClick={() => {
                          form.setValue('settings.customRecipeOutro', null)
                          form.setValue('settings.customRecipeOutro.name', '')
                          form.setValue(
                            'settings.customRecipeOutro.version',
                            ''
                          )
                        }}
                      >
                        Remove Custom Recipe
                      </Button>
                    )}
                  </Box>
                </Then>
                <Else>
                  <Box
                    sx={{
                      display: 'grid',
                      gridColumn: 'span 2',
                      rowGap: 2,
                    }}
                  >
                    <Switch>
                      {languagesSupported.map((orgLang) => (
                        <Case key={orgLang} condition={lang === orgLang}>
                          <VailTextField
                            multiline
                            control={form.control}
                            label="Outro Text"
                            minRows={3}
                            name={`settings.HomeBaseMenuOutro.OutroText.${
                              orgLang as SupportedLocales
                            }.0`}
                          />
                        </Case>
                      ))}
                    </Switch>
                    {fieldArray.fields.map((field, index) => (
                      <Stack
                        key={field.id}
                        alignItems="center"
                        direction="row"
                        justifyContent="center"
                        spacing={2}
                      >
                        <Switch>
                          {languagesSupported.map((orgLang) => (
                            <Case key={orgLang} condition={lang === orgLang}>
                              <VailTextField
                                control={form.control}
                                label="Text"
                                name={`settings.HomeBaseMenuOutro.HomeBasePersistentButtons.${index}.label.${
                                  orgLang as SupportedLocales
                                }`}
                              />
                            </Case>
                          ))}
                        </Switch>
                        <VailSelect
                          control={form.control}
                          label="Type"
                          name={`settings.HomeBaseMenuOutro.HomeBasePersistentButtons.${index}.type`}
                          options={Object.values(ExternalLinkType).map(
                            (option) => ({
                              label: option,
                              value: option,
                            })
                          )}
                        />
                        <VailTextField
                          control={form.control}
                          label="Value"
                          name={`settings.HomeBaseMenuOutro.HomeBasePersistentButtons.${index}.value`}
                        />
                        <IconButton
                          id={safeCypressID(`delete-${index}`)}
                          size="small"
                          sx={{ height: 'fit-content' }}
                          onClick={() => fieldArray.remove(index)}
                        >
                          <Icon name="delete" />
                        </IconButton>
                      </Stack>
                    ))}
                    <Button
                      id={safeCypressID('add-button')}
                      startIcon={<Icon name="add" />}
                      sx={{ alignSelf: 'flex-start' }}
                      variant="outlined"
                      onClick={() =>
                        fieldArray.append({
                          label: { en: '' },
                          type: ExternalLinkType.url,
                          value: '',
                        })
                      }
                    >
                      Add Button
                    </Button>
                  </Box>
                </Else>
              </If>
            </Box>
          </Box>
          <When
            condition={
              form.formState.errors.settings?.HomeBaseMenuOutro
                ?.InteractionsToSurfaceOutroButton?.root?.message
            }
          >
            <Alert severity="error">
              {form.formState.errors.settings?.HomeBaseMenuOutro
                ?.InteractionsToSurfaceOutroButton?.root?.message || ''}
            </Alert>
          </When>
        </VailAccordion>
      </Stack>
    </FormProvider>
  )
}
